import { useSyncExternalStore } from "react";
import {
  type Bilingual,
  type Lang,
  isLang,
  LOCALE,
  preferredLang,
} from "../shared/i18n";

export { bilingual, LANG_NAMES, LANGS, type Lang } from "../shared/i18n";

// The visitor's language: `?lang=` on the address (campaign links; it leaves
// the address bar), else their last choice on this browser, else the browser's.
const KEY = "foco.lang";
function initial(): Lang {
  const url = new URL(window.location.href);
  const asked = url.searchParams.get("lang");
  if (asked !== null) {
    url.searchParams.delete("lang");
    window.history.replaceState(window.history.state, "", url);
    if (isLang(asked)) {
      remember(asked);
      return asked;
    }
  }
  try {
    const stored = localStorage.getItem(KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* storage blocked: follow the browser */
  }
  return preferredLang(
    navigator.languages?.length ? navigator.languages : [navigator.language],
  );
}
function remember(next: Lang) {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* the choice lasts this visit */
  }
}

let current: Lang = initial();
document.documentElement.lang = current;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The language now, for code outside components (request headers, errors). */
export const lang = () => current;
export const locale = () => LOCALE[current];
export function setLang(next: Lang) {
  if (next === current) return;
  current = next;
  remember(next);
  document.documentElement.lang = next;
  for (const listener of listeners) listener();
}
export const useLang = () => useSyncExternalStore(subscribe, lang);
/** A component's text in the visitor's language; it re-renders when the language changes. */
export const useText = <T>(text: Bilingual<T>): T => text[useLang()];
/** Text outside components, in the language now. */
export const text = <T>(text: Bilingual<T>): T => text[current];
